import { useState, useEffect } from 'react';
import { FiDollarSign, FiClock, FiCheckCircle } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import PaymentModal from '../components/payment/PaymentModal';
import RemittanceModal from '../components/payment/RemittanceModal';
import api from '../services/api';

export default function Fees() {
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isRemittanceModalOpen, setIsRemittanceModalOpen] = useState(false);
  const [feesData, setFeesData] = useState([]);
  const [lastPayment, setLastPayment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const fetchFees = async () => {
    try {
      const response = await api.get('/fee/due');
      setFeesData(response.data.data.fees);
      setLastPayment(response.data.last_payment);
    } catch (err) {
      setError('Unable to load fees information. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paymentStatus = searchParams.get('payment');

    if (paymentStatus === 'success') {
      setSuccessMessage('Your payment was successful!\nYour due fees will be updated as soon as the payment is processed.');
    } 

    if (paymentStatus === 'failed') {
      setErrorMessage('Failed!!\nIf this is not consistent with your transaction, please contact the school management.');
    } 

    fetchFees();
  }, []);



  const formatCurrency = (amount) => {
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(numericAmount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDueDate = (month, year) => {
    return new Date(year, new Date(`${month} 1`).getMonth(), 1);
  };

  const totalDue = feesData.reduce((sum, fee) => sum + parseFloat(fee.total_due), 0);

  const handlePaymentSuccess = () => {
    setSuccessMessage('Your payment was successful!\nYour due fees will be updated as soon as the payment is processed.');
    setIsPaymentModalOpen(false);
    setIsRemittanceModalOpen(false);
    fetchFees();
    setTimeout(() => {
      setSuccessMessage('');
    }, 20000);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-4">
          <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          <div className="h-10 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
          <div className="p-4 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-100 dark:bg-gray-700/50 rounded animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="text-red-600 dark:text-red-400">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Monthly Tuition Fees
        </h2>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={() => setIsRemittanceModalOpen(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-primary bg-white border border-primary rounded-md hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-offset-gray-800"
          >
            Register Remittance
          </button>
          <button
            onClick={() => setIsPaymentModalOpen(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:ring-primary-light border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          >
            <FiDollarSign className="h-4 w-4 mr-2" />
            Pay Now
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="mx-4 p-4 rounded-lg bg-green-100 dark:bg-green-900/50 border border-green-500 dark:border-green-500/30">
          <div className="flex items-center justify-center gap-2">
            <FiCheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            <p className="text-green-800 dark:text-green-300 font-medium text-center whitespace-pre-line">
              {successMessage}
            </p>
          </div>
        </div>
      )}
      
      {errorMessage && (
        <div className="mx-4 p-4 rounded-lg bg-red-100 dark:bg-red-900/50 border border-red-500 dark:border-red-500/30">
          <div className="flex items-center justify-center gap-2">
            {/* <FiCheckCircle className="h-5 w-5 text-red-600 dark:text-red-400" /> */}
            <p className="text-red-800 dark:text-red-300 font-medium text-center whitespace-pre-line">
              {errorMessage}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <FiDollarSign className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700 dark:text-red-400 font-medium">Total Due</span>
            </div>
            <span className="text-red-700 dark:text-red-400 font-bold">
              {formatCurrency(totalDue)}
            </span>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {feesData.map((fee) => (
            <div 
              key={fee.id}
              className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
            >
              <div className="flex items-center">
                <FiClock className="h-5 w-5 text-primary mr-2 flex-shrink-0" />
                <div>
                  <div className="text-gray-700 dark:text-gray-300">
                    Tuition Fee - {fee.month} {fee.year}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Due by {formatDate(getDueDate(fee.month, fee.year))}
                  </div>
                </div>
              </div>
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {formatCurrency(fee.total_due)}
              </span>
            </div>
          ))}

          {lastPayment && (
            <div className="flex justify-between items-center p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <div className="flex items-center">
                <FiCheckCircle className="h-5 w-5 text-primary mr-2 flex-shrink-0" />
                <div>
                  <div className="text-gray-700 dark:text-gray-300">
                    Last Payment - {lastPayment.month}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Paid on {formatDate(lastPayment.date)}
                    {lastPayment.status && (
                      <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                        lastPayment.status === 'Completed' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : lastPayment.status === 'Pending'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : lastPayment.status === 'Failed'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}>
                        {lastPayment.status}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {formatCurrency(lastPayment.amount)}
              </span>
            </div>
          )}

          <Link
            to="/payments"
            className="block text-center text-sm text-primary hover:text-primary-dark dark:text-primary-300 dark:hover:text-primary-200 mt-4"
          >
            View Payment History
          </Link>
        </div>
      </div>

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSuccess={handlePaymentSuccess}
        amount={totalDue}
      />

      <RemittanceModal
        isOpen={isRemittanceModalOpen}
        onClose={() => setIsRemittanceModalOpen(false)}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
}